/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-03-14 00:00:00
 *
 *******************************************************/
import React, { Component } from 'react';
import { TextStyle, TextField, Link, Button, Banner } from '@shopify/polaris';
import { DuplicateMinor } from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import {
  navigateToShopifyCustomizeSSR,
  navigateToThemeCodeEditor,
  copyClipBoard
} from 'src/helpers/shopify-integration-helpers';

import BoostSnippetDemo from 'src/assets/images/shopify-integration/BoostSnippetDemo.jpg';
import BoostSnippetDemo2 from 'src/assets/images/shopify-integration/BoostSnippetDemo2.png';
import EditCode from 'src/assets/images/shopify-integration/EditCode.gif';

type Props = {
  onChangeTemplate: Function,
  onManageTemplate: Function
};

type ComposedProps = Props & WithI18nProps;

class BoostSnippetManual extends Component<ComposedProps> {
  state = {
    active: false
  };

  togglePopover = () => {
    this.setState(({ active }) => {
      return { active: !active };
    });
  };

  render() {
    const { isOS20 = true } = this.props;

    return (
      <React.Fragment>
        {isOS20 ? this.renderContentOS20() : this.renderContentOS10()}
      </React.Fragment>
    );
  }

  renderContentOS10 = () => {
    const { i18n, themeId, isOS20 = true } = this.props;

    return (
      <React.Fragment>
        <div className="heading">
          {isOS20
            ? i18n.translate('AdminHTML.ShopifyIntegration.Guide.menuEnableBoostSnippet')
            : i18n.translate('AdminHTML.ShopifyIntegration.Guide.menuEnableBoostSnippetOS10')}
        </div>

        {/* Banner warning */}
        <div className="warning">
          <Banner status="critical">
            {i18n.translate('AdminHTML.ShopifyIntegration.Guide.warningInitSetup')}
          </Banner>
        </div>

        <div className="instruction">
          {/* Step 1 */}
          <div className="step">
            <div style={{ display: 'inline-flex' }}>
              <div className="left-col">
                <TextStyle variation="strong">
                  {i18n.translate('AdminHTML.ShopifyIntegration.Guide.initSetupStep1')}
                </TextStyle>

                <div style={{ padding: '16px 0 0 0px' }}>
                  {i18n.translate('AdminHTML.ShopifyIntegration.Guide.initSetupStep1_instruction', {
                    t1: (
                      <TextStyle variation="strong">
                        {i18n.translate(
                          'AdminHTML.ShopifyIntegration.Guide.initSetupStep1_instruction_t1'
                        )}
                      </TextStyle>
                    )
                  })}
                </div>

                {/* Enable snippet */}
                <div className="enable-snippet">
                  {/* View settings */}
                  <div className="btn-enable">
                    <Button
                      primary
                      onClick={() => {
                        navigateToShopifyCustomizeSSR(themeId, true);
                      }}>
                      {i18n.translate('AdminHTML.ShopifyIntegration.Actions.enableBoostSnippet')}
                    </Button>
                  </div>
                </div>

                {/* Instruction */}
                {this.renderManualInstruction()}
              </div>
              {/* Illustration image */}
              <div className="right-col">
                <img
                  className="thumbnail clickable"
                  src={BoostSnippetDemo2}
                  width={500}
                  height={281}
                  alt=""
                  onClick={() => window.open(BoostSnippetDemo2, '_blank', 'noopener,noreferrer')}
                />
              </div>
            </div>
          </div>

          {/* Step 2 */}
          <div className="step">
            <div style={{ display: 'inline-flex' }}>
              <div className="left-col">
                <TextStyle variation="strong">
                  {i18n.translate('AdminHTML.ShopifyIntegration.Guide.initSetupStep2')}
                </TextStyle>

                <div style={{ padding: '16px 0' }}>
                  {i18n.translate('AdminHTML.ShopifyIntegration.Guide.initSetupStep2_instruction', {
                    t1: (
                      <TextStyle variation="strong">
                        {i18n.translate(
                          'AdminHTML.ShopifyIntegration.Guide.initSetupStep2_instruction_t1'
                        )}
                      </TextStyle>
                    ),
                    t2: (
                      <span className="asset-file">
                        {i18n.translate(
                          'AdminHTML.ShopifyIntegration.Guide.initSetupStep2_instruction_t2'
                        )}
                      </span>
                    )
                  })}
                </div>

                {/* Edit code */}
                <div className="btn-enable">
                  <Button
                    onClick={() => {
                      navigateToThemeCodeEditor(themeId);
                    }}>
                    {i18n.translate('AdminHTML.ShopifyIntegration.Actions.gotoEditCode')}
                  </Button>
                </div>
              </div>
              {/* Illustration image */}
              <div className="right-col">
                <img
                  className="thumbnail clickable"
                  src={EditCode}
                  width={500}
                  height={281}
                  alt=""
                  onClick={() => window.open(EditCode, '_blank', 'noopener,noreferrer')}
                />
              </div>
            </div>

            {/* Code snippet */}
            <div className="code" style={{ fontSize: '12px', marginTop: '16px' }}>
              <span className="copy custom-style">
                <Button
                  icon={DuplicateMinor}
                  plain
                  onClick={() => this.copyCodeSnippetClipBoard()}
                />
              </span>
              <span>{`{% comment %} PLEASE DO NOT EDIT THIS FILE {% endcomment %}`}</span>
              <br />
              <span>{`{% case type %}`}</span>
              <br />
              <span>&nbsp;&nbsp;{`{% when 'product-filter' %}`}</span>
              <br />
              <span>
                &nbsp;&nbsp;&nbsp;&nbsp;{`<div class="boost-sd__filter-product-list"></div>`}
              </span>
              <br />
              <span>&nbsp;&nbsp;&nbsp;&nbsp;{`<script>`}</span>
              <br />
              <span>
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                {`if (window.boostSDTaeUtils && window.boostSDTaeUtils.initCollectionFilter)`}
              </span>
              <br />
              <span>
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                {`{window.boostSDTaeUtils.initCollectionFilter();}`}
              </span>
              <br />
              <span>&nbsp;&nbsp;&nbsp;&nbsp;{`</script>`}</span>
              <br />
              <span>&nbsp;&nbsp;{`{% when 'recommendation' %}`}</span>
              <br />
              <span>
                &nbsp;&nbsp;&nbsp;&nbsp;{`<div id="boost-sd-widget-{{- widgetId -}}"></div>`}
              </span>
              <br />
              <span>{`{% endcase %}`}</span>
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  };

  renderContentOS20 = () => {
    const { i18n, themeId } = this.props;

    return (
      <React.Fragment>
        <div className="heading">
          {i18n.translate('AdminHTML.ShopifyIntegration.Guide.menuEnableBoostSnippet')}
        </div>

        <div className="boost-snippet">
          {/* Instruction */}
          <div className="left-col">
            <div>
              {i18n.translate('AdminHTML.ShopifyIntegration.EnableBoostSnippet.heading', {
                t1: (
                  <TextStyle variation="strong">
                    {i18n.translate('AdminHTML.ShopifyIntegration.EnableBoostSnippet.heading_t1')}
                  </TextStyle>
                )
              })}
            </div>

            {/* Enable snippet */}
            <div className="enable-snippet">
              {/* View settings */}
              <div className="btn-enable">
                <Button
                  primary
                  onClick={() => {
                    navigateToShopifyCustomizeSSR(themeId, true);
                  }}>
                  {i18n.translate('AdminHTML.ShopifyIntegration.Actions.enableBoostSnippet')}
                </Button>
              </div>
            </div>

            {/* Instruction */}
            {this.renderManualInstruction()}
          </div>

          {/* Illustration image */}
          <div className="right-col">
            <img
              className="thumbnail clickable"
              src={BoostSnippetDemo}
              width={500}
              height={281}
              alt=""
              onClick={() => window.open(BoostSnippetDemo, '_blank', 'noopener,noreferrer')}
            />
          </div>
        </div>
      </React.Fragment>
    );
  };

  renderManualInstruction = () => {
    const { i18n, themeId, templateId } = this.props;

    return (
      <div className="manual-instruction">
        <p>
          {i18n.translate('AdminHTML.ShopifyIntegration.EnableBoostSnippet.instruction', {
            t1: (
              <TextStyle variation="strong">
                {i18n.translate('AdminHTML.ShopifyIntegration.EnableBoostSnippet.instruction_t1')}
              </TextStyle>
            )
          })}
        </p>
        <ul>
          <li>
            {i18n.translate('AdminHTML.ShopifyIntegration.EnableBoostSnippet.instruction_step1', {
              t1: (
                <TextStyle variation="strong">
                  {i18n.translate(
                    'AdminHTML.ShopifyIntegration.EnableBoostSnippet.instruction_step1_t1'
                  )}
                </TextStyle>
              ),
              t2: (
                <Link
                  external
                  onClick={() => {
                    navigateToShopifyCustomizeSSR(themeId, false);
                  }}>
                  {i18n.translate(
                    'AdminHTML.ShopifyIntegration.EnableBoostSnippet.instruction_step1_t2'
                  )}
                </Link>
              )
            })}
          </li>
          <li>
            {i18n.translate('AdminHTML.ShopifyIntegration.EnableBoostSnippet.instruction_step2', {
              t1: (
                <TextStyle variation="strong">
                  {i18n.translate(
                    'AdminHTML.ShopifyIntegration.EnableBoostSnippet.instruction_step2_t1'
                  )}
                </TextStyle>
              )
            })}
          </li>
          <li>
            {i18n.translate('AdminHTML.ShopifyIntegration.EnableBoostSnippet.instruction_step3', {
              t1: (
                <TextStyle variation="strong">
                  {i18n.translate(
                    'AdminHTML.ShopifyIntegration.EnableBoostSnippet.instruction_step3_t1'
                  )}
                </TextStyle>
              )
            })}
          </li>
        </ul>
        <div>
          <TextField
            value={templateId}
            disabled={true}
            suffix={
              <span className="copy">
                <Button
                  icon={DuplicateMinor}
                  plain
                  onClick={() => copyClipBoard(i18n, templateId)}
                />
              </span>
            }
          />
        </div>
      </div>
    );
  };

  copyCodeSnippetClipBoard = () => {
    const { i18n } = this.props;

    const code = `
{% comment %} PLEASE DO NOT EDIT THIS FILE {% endcomment %}
{% case type %}
  {% when 'product-filter' %}
    <div class="boost-sd__filter-product-list"></div>
    <script>
      if (window.boostSDTaeUtils && window.boostSDTaeUtils.initCollectionFilter) 
      {window.boostSDTaeUtils.initCollectionFilter();}
    </script>
  {% when 'recommendation' %}
    <div id="boost-sd-widget-{{- widgetId -}}"></div>
{% endcase %}`;

    copyClipBoard(i18n, code);
  };
}

export default withI18n()(BoostSnippetManual);
